import type React from "react";
import { createContext, useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import type { User } from "@/features/Auth/service/api";
import { useSession, useSignOutMutation } from "@/features/Auth/service/query";
import { useOrganizationQuery } from "@/features/organization/service/query";
import type { OrgType } from "@/types/orgTypes";

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: User | null;
  role: "owner" | "admin" | "member" | "guest-member" | null;
  orgId: string | null;
  orgType: OrgType | null;
  signOut: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const { data: sessionData, isLoading } = useSession();
  const signOutMutation = useSignOutMutation();
  const navigate = useNavigate();
  const location = useLocation();

  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [hasCheckedAuth, setHasCheckedAuth] = useState(false);

  const activeOrg = sessionData?.user?.organizations[0];
  const orgId = activeOrg?.id ?? null;

  const { data: orgData } = useOrganizationQuery(orgId);
  const orgType = orgData?.data?.type ?? null;

  useEffect(() => {
    if (!isLoading) {
      const hasSession = !!sessionData?.session;
      setIsAuthenticated(hasSession);
      setHasCheckedAuth(true);

      localStorage.setItem("orgId", activeOrg?.id ?? "");

      const isProtectedRoute = !(
        location.pathname.startsWith("/auth") ||
        location.pathname.startsWith("/bp/")
      );
      const isApiRoute = location.pathname.startsWith("/api/");

      if (!hasSession && isProtectedRoute && !isApiRoute) {
        const callbackUrl = location.pathname + location.search;
        navigate(`/auth/login?callbackUrl=${encodeURIComponent(callbackUrl)}`, {
          replace: true,
        });
      }
    }
  }, [sessionData, isLoading, navigate, location, activeOrg]);

  const signOut = () => {
    signOutMutation.mutate();
  };

  const value = {
    isAuthenticated,
    isLoading: isLoading || !hasCheckedAuth,
    user: sessionData?.user || null,
    role: (activeOrg?.role as AuthContextType["role"]) || null,
    orgId,
    orgType,
    signOut,
  };

  if (!hasCheckedAuth || isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
